<template>
  <div class="documentation-anchor">
    <button
      ref="buttonElement"
      type="button"
      class="documentation-anchor__icon button-icon"
      :class="{ filled: !empty }"
      @pointerdown="onCircleMouseDown"
      @click="onClick"
    >
      <lucide-icon name="doc" />
    </button>

    <documentation-content
      v-if="open"
      :value="docValue"
      :x="anchorPosition.left"
      :y="anchorPosition.top"
      :x-offset="20"
      :non-closing-elements="[buttonElement]"
      @update:value="onInput"
      @hide="open = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useModelStore } from "@editor/items/model/model.store";

import LucideIcon from "@core/ui/LucideIcon.vue";
import DocumentationContent from "./DocumentationContent.vue";

import type { ItemType } from "@editor/items/ItemOfStore";
import type { Handleable } from "@core/types/Events";

const { object, prop, updateStore = true } = defineProps<{
  object: {
    type?: ItemType;
    id?: number;
    docs?: Record<string, string>;
  };
  prop: string;
  updateStore?: boolean;
}>();

const emit = defineEmits<{
  (event: "update:value", value: string): void;
}>();

const buttonElement = ref<HTMLButtonElement>();

const open = ref(false);
const anchorPosition = ref({ left: 0, top: 0 });

const empty = computed(() => {
  if (typeof object?.docs?.[prop] != "string") return true;
  const text = object.docs[prop].split("<p></p>").join("");
  return text.trim() === "";
});
const docValue = computed(() => {
  return object?.docs?.[prop] ?? "";
});
const store = computed(() => {
  if (object.type == null) return null;
  const storesByType = useModelStore().storesByType;
  if (!(object.type in storesByType)) return null;
  return storesByType?.[object.type as ItemType];
});

function onCircleMouseDown(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
  if (open.value) {
    mouseEvent.preventDefault();
    mouseEvent.stopImmediatePropagation();
  }
}
function onClick(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.hasBeenHandled = true;
  if (open.value) {
    open.value = false;
  } else {
    openField();
  }
}
function openField() {
  open.value = true;
  if (buttonElement.value) {
    anchorPosition.value = buttonElement.value.getBoundingClientRect();
  }
}
function onInput(value: string) {
  if (updateStore) {
    updateObjectDoc(value);
  }

  emit("update:value", value);
}
function updateObjectDoc(value: string) {
  const objectDocs = { ...(object?.docs || {}) };
  objectDocs[prop] = value;

  if (store.value && object.id != null) {
    store.value.update({
      id: object.id,
      docs: objectDocs
    });
  }
}
</script>

<style lang="scss">
.documentation-anchor {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  grid-area: docAnchor;
  font-size: 18px;

  &__icon {
    font-size: 1em;
    display: block;
    width: 18px;
    height: 1em;
    padding: 0;
    color: var(--color-text-light);
    flex-shrink: 0;
    min-height: 0;
    margin-bottom: -2px;

    svg {
      stroke-width: 2px;
      stroke: currentColor;
      display: block;
    }
    &.filled {
      color: var(--color-action);
    }
    &:active {
      transform: translateY(1px);
    }
  }

  &:hover &__icon {
    color: var(--color-text);
  }
  &:hover &__icon.filled {
    color: var(--color-action);
    filter: drop-shadow(1px 1px 0 var(--color-action-transparent));
  }
}
</style>
